import { isNil } from "lodash-es";
import { booleanType, ensure, ensureArray, numberType, stringType, TType } from "types";

import {
  configDatabaseRecordType,
  configIdType,
  configPageableMetaType,
  configSystemRecordType,
  IDatabaseRecord,
  idType,
  IId,
  IPageable,
  IPartialPageable,
  ISystemRecord,
} from "@/core/api";
import { dateTimeType, dateType, TDate, TDateTime } from "@/core/dates";
import { htmlType, THtml } from "@/core/html";
import { attachmentType, IAttachment } from "@/data/attachments";
import { ICodelist } from "@/data/codelists";
import {
  configConsultantProfileType,
  consultantProfileWorkStyleType,
  IConsultantProfile,
  IWorkStyle,
} from "@/data/consultant-profiles";
import { IJobRequest, IQuestion, jobRequestType, questionType } from "@/data/job-requests";

import { JobApplicationStepKey } from "./types/JobApplicationStepKey";

interface IJobApplicationConsultantProfile extends IConsultantProfile {
  parent: IId;
}

const configJobApplicationConsultantProfileType: TType<IJobApplicationConsultantProfile> = {
  ...configConsultantProfileType,
  parent: idType,
};

const jobApplicationConsultantProfileType = ensure<IJobApplicationConsultantProfile>(
  configJobApplicationConsultantProfileType,
);

export interface IJobApplicationConsent extends IId {
  content: THtml;
  dateFrom: TDate;
  dateTo: TDate;
}

const configJobApplicationConsent: TType<IJobApplicationConsent> = {
  ...configIdType,
  content: htmlType(""),
  dateFrom: dateType(),
  dateTo: dateType(),
};

export const jobApplicationConsentType = ensure(configJobApplicationConsent);

export interface IJobApplicationRate {
  currency?: string;
  ratePerHour: number;
  rateType?: "HOURLY" | "FIXED";
}

const configJobApplicationRateType: TType<IJobApplicationRate> = {
  currency: stringType(""),
  ratePerHour: numberType(),
  rateType: stringType(""),
};

const jobApplicationRateType = ensure(configJobApplicationRateType);

export interface IJobApplicationRateDto {
  currency?: string;
  ratePerHour?: number;
}

export interface IJobApplicationComment {
  authorFirstName: string | null;
  authorLastName: string | null;
  commentCreatedDate: string | null;
  externalComment: string | null;
}

const configJobApplicationCommentType: TType<IJobApplicationComment> = {
  authorFirstName: v => (isNil(v) ? null : stringType("")(v)),
  authorLastName: v => (isNil(v) ? null : stringType("")(v)),
  commentCreatedDate: dateType(),
  externalComment: v => (isNil(v) ? null : stringType("")(v)),
};

const jobApplicationCommentType = ensure(configJobApplicationCommentType);

export interface IJobApplicationDto {
  consultantProfile: IId;
  company: IId;
  motivation: string;
  rate: IJobApplicationRateDto;
}

export interface IJobApplicationDraftDto extends Partial<IJobApplicationDto> {}

export type TJobApplicationStatuses =
  | "SUBMITTED"
  | "IN_REVIEW"
  | "PRESENTED_TO_CLIENT"
  | "INTERVIEW"
  | "ACCEPTED"
  | "REJECTED"
  | "WITHDRAWN"
  | "DRAFT"
  | "DELETED"
  | "EXPIRED";

export interface IAnswer {
  question: IQuestion;
  answer: boolean | null;
  elaboration: string;
}

const configAnswerType: TType<IAnswer> = {
  question: questionType,
  elaboration: stringType(""),
  answer: booleanType(null),
};

const answerType = ensure(configAnswerType);

export interface IJobApplication extends Omit<IJobApplicationDto, "rate">, IDatabaseRecord, ISystemRecord {
  consent: IJobApplicationConsent | null;
  consultantProfile: IJobApplicationConsultantProfile;
  jobRequest: IJobRequest;
  rate: IJobApplicationRate;
  status: TJobApplicationStatuses;
  answers: IAnswer[];
  attachments: IAttachment[];
  submittedDate: TDateTime;
  externalSystemId?: string;
  comment?: IJobApplicationComment;
  workStyles?: IWorkStyle[] | null;
  step?: JobApplicationStepKey | null;
  updateCvs?: boolean;
  updateAvailability?: boolean;
  updateWorkstyles?: boolean;
  updateContactPersonPhoneNumber?: boolean;
}

const configJobApplicationType: TType<IJobApplication> = {
  ...configDatabaseRecordType,
  ...configSystemRecordType,
  consent: v => (isNil(v) ? null : jobApplicationConsentType(v)),
  consultantProfile: jobApplicationConsultantProfileType,
  company: idType,
  jobRequest: jobRequestType,
  motivation: stringType(""),
  rate: v => (isNil(v) ? {} : jobApplicationRateType(v)),
  status: stringType(""),
  answers: ensureArray(answerType),
  attachments: ensureArray(attachmentType),
  submittedDate: dateTimeType(),
  externalSystemId: stringType(undefined),
  comment: v => (isNil(v) ? null : jobApplicationCommentType(v)),
  workStyles: v => (isNil(v) || v.length < 7 ? [] : ensureArray(consultantProfileWorkStyleType)(v)),
  step: stringType(null),
  updateCvs: booleanType(null),
  updateAvailability: booleanType(null),
  updateWorkstyles: booleanType(null),
  updateContactPersonPhoneNumber: booleanType(null),
};

export const jobApplicationType = ensure(configJobApplicationType);

export interface IPageableJobApplication extends IPageable<IJobApplication[]> {}

export const pageableJobApplicationTypeConfig: TType<IPageableJobApplication> = {
  ...configPageableMetaType,
  content: ensureArray(jobApplicationType),
};

export const pageableJobApplicationType = ensure(pageableJobApplicationTypeConfig);

export interface IPartialPageableJobApplication extends IPartialPageable<IJobApplication[]> {}

export interface IJobApplicationFilterValues {
  statuses?: ICodelist[];
  query?: string;
  contactPersonProfileIds?: Array<IConsultantProfile["id"]>;
}
